/**
 * 坐标转换工具类
 * 负责WGS84和GCJ02坐标系之间的批量转换
 * 
 * 使用说明：
 * - WGS84：GPS坐标系，用户界面显示使用
 * - GCJ02：火星坐标系，数据库存储使用
 * - API格式：[[lng, lat], [lng, lat], ...]
 */

import api from '@/api/index'
import language from '@/utils/language'
import store from '@/store'

class CoordinateConverter {
  constructor() {
    // 坐标转换缓存，避免重复转换
    this.cache = new Map()
    // 缓存最大数量
    this.maxCacheSize = 1000
  }

  /**
   * 获取翻译文本
   * @param {string} key - 翻译键
   * @param {Object} params - 参数对象
   * @returns {string} 翻译后的文本
   */
  t(key, params = {}) {
    const lang = store.state.app.language
    let text = language[key][lang]
    // 替换参数
    Object.keys(params).forEach(param => {
      text = text.replace(`{${param}}`, params[param])
    })
    return text
  }

  /**
   * 批量转换 GCJ02 → WGS84
   * @param {Array<{lon: number, lat: number}>} coordinates - GCJ02坐标数组
   * @param {Object} vueContext - Vue组件上下文（用于API调用）
   * @returns {Promise<Array<{lon: number, lat: number}>>} WGS84坐标数组
   */
  async batchGcj02ToWgs84(coordinates, vueContext) {
    if (!coordinates || coordinates.length === 0) {
      return []
    }

    try {
      // 格式化为API所需格式
      const batchInput = this.formatCoordinatesForAPI(coordinates)

      // 验证输入数据
      if (batchInput.some(pair => pair.some(v => Number.isNaN(v)))) {
        throw new Error(this.t('coordConverterInvalidData'))
      }

      // 调用批量转换API
      const res = await api.postData(
        'task-server/coordinate/batchGcj02ToWgs84',
        batchInput,
        vueContext
      )

      // 解析响应
      const data = this._parseAPIResponse(res)
      
      if (!data || data.length !== batchInput.length) {
        throw new Error(this.t('coordConverterLengthMismatch'))
      }

      // 解析为坐标对象数组
      const result = this.parseCoordinatesFromAPI(data)

      // 验证结果
      if (result.some(p => Number.isNaN(p.lat) || Number.isNaN(p.lon))) {
        throw new Error(this.t('coordConverterInvalidResult'))
      }

      return result
    } catch (error) {
      console.error(this.t('coordConverterGcj02ToWgs84Failed') + ':', error)
      throw new Error(this.t('coordConverterFailed') + ': ' + error.message)
    }
  }

  /**
   * 批量转换 WGS84 → GCJ02
   * @param {Array<{lon: number, lat: number}>} coordinates - WGS84坐标数组
   * @param {Object} vueContext - Vue组件上下文（用于API调用）
   * @returns {Promise<Array<{lon: number, lat: number}>>} GCJ02坐标数组
   */
  async batchWgs84ToGcj02(coordinates, vueContext) {
    if (!coordinates || coordinates.length === 0) {
      return []
    }

    try {
      // 格式化为API所需格式
      const batchInput = this.formatCoordinatesForAPI(coordinates)

      // 验证输入数据
      if (batchInput.some(pair => pair.some(v => Number.isNaN(v)))) {
        throw new Error(this.t('coordConverterInvalidData'))
      }

      // 调用批量转换API
      const res = await api.postData(
        'task-server/coordinate/batchWgs84ToGcj02',
        batchInput,
        vueContext
      )

      // 解析响应
      const data = this._parseAPIResponse(res)
      
      if (!data || data.length !== batchInput.length) {
        throw new Error(this.t('coordConverterLengthMismatch'))
      }

      // 解析为坐标对象数组
      const result = this.parseCoordinatesFromAPI(data)

      // 验证结果
      if (result.some(p => Number.isNaN(p.lat) || Number.isNaN(p.lon))) {
        throw new Error(this.t('coordConverterInvalidResult'))
      }

      return result
    } catch (error) {
      console.error(this.t('coordConverterWgs84ToGcj02Failed') + ':', error)
      throw new Error(this.t('coordConverterFailed') + ': ' + error.message)
    }
  }

  /**
   * 格式化坐标数组为API所需格式
   * @param {Array<{lon: number, lat: number}>} coordinates
   * @returns {Array<[number, number]>} [[lng, lat], ...]
   */
  formatCoordinatesForAPI(coordinates) {
    return coordinates.map(coord => {
      const lon = parseFloat(coord.lon || coord.longitude || coord.lng)
      const lat = parseFloat(coord.lat || coord.latitude)
      return [lon, lat]
    })
  }

  /**
   * 解析API返回的坐标数组
   * @param {Array<[number, number]>} apiResponse - [[lng, lat], ...]
   * @returns {Array<{lon: number, lat: number}>}
   */
  parseCoordinatesFromAPI(apiResponse) {
    return apiResponse.map(pair => {
      const lon = Array.isArray(pair) ? parseFloat(pair[0]) : NaN
      const lat = Array.isArray(pair) ? parseFloat(pair[1]) : NaN
      return { lon, lat }
    })
  }

  /**
   * 解析API响应
   * @private
   */
  _parseAPIResponse(res) {
    // 兼容两种响应格式
    if (res && res.success && Array.isArray(res.data)) {
      return res.data
    }
    if (res && res.state === 0 && Array.isArray(res.data)) {
      return res.data
    }
    return null
  }

  /**
   * 获取缓存键
   * @private
   */
  _getCacheKey(lon, lat, direction) {
    return `${direction}:${lon.toFixed(6)},${lat.toFixed(6)}`
  }

  /**
   * 清空缓存
   */
  clearCache() {
    this.cache.clear()
  }

  /**
   * 获取缓存大小
   */
  getCacheSize() {
    return this.cache.size
  }
}

// 导出单例
export default new CoordinateConverter()
